import React, { useEffect, useRef, useState } from 'react';
import { Upload, Loader2, CheckCircle2, AlertTriangle, HelpCircle, MailWarning, Landmark } from 'lucide-react';
import api from '@/lib/axios';
import { formatDeNumber } from '@/lib/utils';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import DateInput from '@/components/ui/date-input';

const isoHeute = (versatz) => {
  const tag = new Date();
  tag.setDate(tag.getDate() + versatz);
  const zwei = (n) => String(n).padStart(2, '0');
  return `${tag.getFullYear()}-${zwei(tag.getMonth() + 1)}-${zwei(tag.getDate())}`;
};

const euro = (wert) => {
  const zahl = Number(wert);
  if (!Number.isFinite(zahl)) return '';
  return `${formatDeNumber(zahl)} €`;
};

const bankname = (bank) => {
  const konto = bank.account_iban
    ? `•••• ${bank.account_iban.replace(/\s/g, '').slice(-4)}`
    : 'Konto fehlt';
  return `${bank.label || bank.bank_code} · ${konto}`;
};

const buchungsbank = (zeile) => {
  const iban = zeile.account_iban || zeile.iban || '';
  const konto = iban ? `•••• ${iban.replace(/\s/g, '').slice(-4)}` : '';
  return [zeile.bank_label || 'Kontoauszug-Import', konto].filter(Boolean).join(' · ');
};

export const SepaStatementTab = ({ onImported }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [autoRemind, setAutoRemind] = useState(true);
  const [result, setResult] = useState(null);
  const [banken, setBanken] = useState([]);
  const [bankId, setBankId] = useState('');
  const [von, setVon] = useState(isoHeute(-35));
  const [bis, setBis] = useState(isoHeute(0));
  const [pin, setPin] = useState('');
  const [holen, setHolen] = useState(false);
  const [schritt, setSchritt] = useState('pin');
  const [sessionId, setSessionId] = useState('');
  const [hinweis, setHinweis] = useState('');
  const [entkoppelt, setEntkoppelt] = useState(false);
  const [tan, setTan] = useState('');
  const [verfahren, setVerfahren] = useState([]);
  const [wartezeit, setWartezeit] = useState(0);
  const [staende, setStaende] = useState([]);
  const [bewegungen, setBewegungen] = useState([]);
  const [bewegungsBank, setBewegungsBank] = useState('');
  const [merken, setMerken] = useState(false);
  const [merkenGespeichert, setMerkenGespeichert] = useState(false);

  const aktuelle = banken.find((z) => z.id === bankId) || banken[0];
  const schluessel = aktuelle?.auth_method === 'keyfile';
  const bewegungsKonten = Array.from(new Map(bewegungen.map((b) => [
    b.connection_id || 'import',
    {
      id: b.connection_id || 'import',
      label: buchungsbank(b),
    },
  ])).values());
  const sichtbareBewegungen = bewegungsBank
    ? bewegungen.filter((b) => (b.connection_id || 'import') === bewegungsBank)
    : bewegungen;

  const ladeStand = () => {
    api.get('/sepa/statement/balances').then((r) => setStaende(r.data || [])).catch(() => {});
    api.get('/sepa/statement/movements').then((r) => setBewegungen(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    api.get('/sepa/fints/connections')
      .then((r) => {
        const liste = Array.isArray(r.data) ? r.data : [];
        setBanken(liste);
        if (liste[0]) {
          setBankId(liste[0].id);
          setMerken(!!liste[0].auto_statement);
          setMerkenGespeichert(!!liste[0].auto_statement);
        }
      })
      .catch(() => {});
    ladeStand();
  }, []);

  useEffect(() => {
    if (wartezeit <= 0) return undefined;
    const timer = window.setInterval(() => {
      setWartezeit((wert) => Math.max(0, wert - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [wartezeit]);

  const uebernimmBank = (daten) => {
    if (daten.status === 'tan_wahl') {
      setVerfahren(daten.mechanisms || []);
      setSchritt('tan_wahl');
      return;
    }
    if (daten.status === 'tan_medium') {
      setSessionId(daten.session_id || '');
      setVerfahren(daten.mechanisms || []);
      setSchritt('tan_medium');
      return;
    }
    if (daten.status === 'tan' || daten.status === 'vop') {
      setSessionId(daten.session_id || '');
      setHinweis(daten.challenge || '');
      setEntkoppelt(!!daten.decoupled);
      setWartezeit(Number(daten.retry_after) || 0);
      setTan('');
      setSchritt('tan');
      return;
    }
    setSchritt('pin');
    setPin('');
    setResult(daten);
    if (typeof daten.auto_statement === 'boolean') {
      setMerken(daten.auto_statement);
      setMerkenGespeichert(daten.auto_statement);
      setBanken((prev) => prev.map((z) => (
        z.id === (aktuelle && aktuelle.id) ? { ...z, auto_statement: daten.auto_statement } : z
      )));
    }
    ladeStand();
    const rueck = daten.chargebacks?.length || 0;
    let msg = `${daten.matched?.length || 0} Zahlungen zugeordnet, ${rueck} Rücklastschriften erkannt`;
    if (rueck > 0) msg += '. Die Verwaltung wurde benachrichtigt';
    toast.success(msg);
    if (rueck > 0) onImported?.();
  };

  const vonDerBank = async (extra = {}) => {
    if (!pin.trim() && schritt === 'pin') {
      toast.error(schluessel ? 'Das Passwort der Schlüsseldatei fehlt.' : 'Die PIN fehlt.');
      return;
    }
    setHolen(true);
    try {
      if (extra.tan_security_function && aktuelle?.id) {
        await api.put(`/sepa/fints/connections/${aktuelle.id}`, {
          bank_code: aktuelle.bank_code,
          endpoint_url: aktuelle.endpoint_url,
          bank_user: aktuelle.bank_user,
          customer_id: aktuelle.customer_id || '',
          account_iban: aktuelle.account_iban || '',
          account_bic: aktuelle.account_bic || '',
          label: aktuelle.label || '',
          auth_method: aktuelle.auth_method || 'tan',
          tan_security_function: extra.tan_security_function,
        });
      }
      const r = await api.post('/sepa/fints/statement', {
        pin: pin.trim(),
        connection_id: aktuelle?.id || '',
        von,
        bis,
        merken,
      });
      uebernimmBank(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Der Kontoauszug konnte nicht geholt werden.');
    } finally {
      setHolen(false);
    }
  };

  const tanSenden = async (wert = null) => {
    setHolen(true);
    try {
      const r = await api.post('/sepa/fints/continue', {
        session_id: sessionId,
        tan: wert !== null ? wert : (entkoppelt ? '' : tan.trim()),
      });
      uebernimmBank(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Die Bank hat den Abruf nicht angenommen.');
    } finally {
      setHolen(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/sepa/statement/import?auto_remind=${autoRemind ? 'true' : 'false'}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      const sent = r.data.reminders_sent?.length || 0;
      const queued = r.data.reminders_queued?.length || 0;
      const failed = r.data.reminders_failed?.length || 0;
      let msg = `${r.data.matched.length} Zahlungen zugeordnet, ${r.data.chargebacks.length} Rücklastschriften erkannt`;
      if (r.data.chargebacks.length > 0) msg += '. Die Verwaltung wurde benachrichtigt';
      if (autoRemind && r.data.chargebacks.length > 0) {
        msg += ` · ${sent + queued} Mahnung(en) ausgelöst`;
        if (failed) msg += `, ${failed} fehlgeschlagen`;
      }
      toast.success(msg);
      onImported?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import fehlgeschlagen');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remindSent = result?.reminders_sent || [];
  const remindQueued = result?.reminders_queued || [];
  const remindFailed = result?.reminders_failed || [];
  const remindSkipped = result?.reminders_skipped || [];

  return (
    <div className="space-y-4">
      <Card data-testid="statement-bank-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Landmark className="w-4 h-4" /> Kontostand und Bewegungen von der Bank
          </CardTitle>
          <CardDescription>
            Der Abruf nutzt die hinterlegte Bankverbindung. Eingänge werden den Rechnungen
            zugeordnet. Eine zurückgegebene Lastschrift erscheint unter Rückbuchungen, und die
            Verwaltung wird benachrichtigt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {staende.length > 0 && (
            <div className="flex flex-wrap gap-3" data-testid="statement-balances">
              {staende.map((s) => (
                <div
                  key={s.iban}
                  className="rounded-md border border-slate-200 px-3 py-2"
                  data-testid={`statement-balance-${s.connection_id || 'import'}`}
                >
                  <div className="text-sm font-medium text-slate-700">{buchungsbank(s)}</div>
                  <div className="text-xs text-slate-500">{s.as_of ? `Stand ${s.as_of.split('-').reverse().join('.')}` : 'Kontostand'}</div>
                  <div className="text-lg font-semibold text-slate-800">{euro(s.booked)}</div>
                </div>
              ))}
            </div>
          )}
          {banken.length > 1 && schritt === 'pin' && (
            <div>
              <Label htmlFor="statement-bank">Bank</Label>
              <select
                id="statement-bank"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={bankId}
                onChange={(e) => {
                  const id = e.target.value;
                  setBankId(id);
                  const z = banken.find((b) => b.id === id);
                  setMerken(!!z?.auto_statement);
                  setMerkenGespeichert(!!z?.auto_statement);
                }}
                data-testid="statement-bank"
              >
                {banken.map((z) => (
                  <option key={z.id} value={z.id} label={bankname(z)} />
                ))}
              </select>
            </div>
          )}
          {banken.length === 1 && schritt === 'pin' && (
            <div data-testid="statement-bank-single">
              <Label>Bankkonto für diesen Abruf</Label>
              <div className="flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm">
                {bankname(banken[0])}
              </div>
            </div>
          )}
          {schritt === 'pin' && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="statement-von">Von</Label>
                  <DateInput id="statement-von" value={von} onChange={(e) => setVon(e.target.value)} data-testid="statement-von" />
                </div>
                <div>
                  <Label htmlFor="statement-bis">Bis</Label>
                  <DateInput id="statement-bis" value={bis} onChange={(e) => setBis(e.target.value)} data-testid="statement-bis" />
                </div>
              </div>
              <div>
                <Label htmlFor="statement-pin">{schluessel ? 'Passwort der Schlüsseldatei' : 'PIN'}</Label>
                <Input
                  id="statement-pin"
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  autoComplete="off"
                  data-testid="statement-pin"
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="statement-auto-toggle">
                <input
                  type="checkbox"
                  className="mt-1 rounded border-slate-300"
                  checked={merken}
                  data-testid="statement-auto"
                  onChange={async (e) => {
                    const an = e.target.checked;
                    setMerken(an);
                    if (an || !aktuelle?.id || !merkenGespeichert) return;
                    try {
                      await api.post('/sepa/fints/statement/auto', {
                        connection_id: aktuelle.id,
                        enabled: false,
                      });
                      setMerkenGespeichert(false);
                      setBanken((prev) => prev.map((z) => (
                        z.id === aktuelle.id ? { ...z, auto_statement: false } : z
                      )));
                    } catch (err) {
                      setMerken(true);
                      toast.error(err.response?.data?.detail || 'Der automatische Abruf ließ sich nicht abschalten.');
                    }
                  }}
                />
                <span>
                  <Label className="cursor-pointer">Jeden Morgen um 8:00 automatisch abgleichen</Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {merkenGespeichert
                      ? 'Läuft jeden Morgen um 8:00. Das Passwort liegt verschlüsselt und gilt nur für den Kontoauszug.'
                      : 'Das Passwort wird beim nächsten erfolgreichen Abruf gespeichert und gilt nur für den Kontoauszug.'}
                    {!schluessel && merken ? ' Wenn die Bank eine TAN verlangt, kommt ein Hinweis an die Verwaltung.' : ''}
                  </p>
                </span>
              </label>
              <Button type="button" onClick={() => vonDerBank()} disabled={holen} data-testid="statement-fetch-btn">
                {holen ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Landmark className="w-4 h-4 mr-2" />}
                Von der Bank holen
              </Button>
            </>
          )}
          {schritt === 'tan_wahl' && (
            <div className="space-y-2">
              <p className="text-sm text-slate-700">Welches TAN-Verfahren soll die Bank verwenden?</p>
              {verfahren.map((v) => (
                <Button
                  key={v.id}
                  type="button"
                  variant="outline"
                  className="w-full justify-start"
                  disabled={holen}
                  onClick={() => vonDerBank({ tan_security_function: v.id })}
                >
                  {v.name}
                </Button>
              ))}
            </div>
          )}
          {schritt === 'tan_medium' && (
            <div className="space-y-2">
              <p className="text-sm text-slate-700">Welches pushTAN-Gerät soll die Bank verwenden?</p>
              {verfahren.map((v) => (
                <Button
                  key={v.id}
                  type="button"
                  variant="outline"
                  className="w-full justify-start"
                  disabled={holen}
                  onClick={() => tanSenden(v.id)}
                  data-testid={`statement-medium-${v.id}`}
                >
                  {v.name}
                </Button>
              ))}
            </div>
          )}
          {schritt === 'tan' && (
            <div className="space-y-3">
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{hinweis}</p>
              {entkoppelt ? (
                <p className="text-sm text-slate-600">Bitte den Abruf in der Banking-App freigeben.</p>
              ) : (
                <div>
                  <Label htmlFor="statement-tan">TAN</Label>
                  <Input id="statement-tan" value={tan} onChange={(e) => setTan(e.target.value)} data-testid="statement-tan" />
                </div>
              )}
              <Button
                type="button"
                onClick={() => tanSenden()}
                disabled={holen || (entkoppelt && wartezeit > 0)}
                data-testid="statement-tan-btn"
              >
                {holen ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {entkoppelt && wartezeit > 0
                  ? `Statusabfrage in ${wartezeit} s`
                  : (entkoppelt ? 'In der App bestätigt' : 'Abruf fortsetzen')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {result?.chargebacks?.length > 0 && (
        <Card className="border-rose-200 bg-rose-50" data-testid="statement-verwaltung-warnung">
          <CardContent className="py-3 text-sm text-rose-800">
            {result.chargebacks.length === 1 ? 'Eine Lastschrift wurde zurückgegeben.' : `${result.chargebacks.length} Lastschriften wurden zurückgegeben.`}
            {' '}Die Verwaltung wurde benachrichtigt. Die offenen Fälle stehen unter Rückbuchungen.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Kontoauszug importieren (CAMT.053)</CardTitle>
          <CardDescription>
            Laden Sie den Kontoauszug aus Ihrem Online-Banking (Format CAMT.053-XML) hoch.
            Zahlungseingänge werden den Rechnungen zugeordnet, Rücklastschriften erkannt.
            Optional startet automatisch die Stufe-1-Zahlungserinnerung (14 Tage, E-Mail mit Rechnungs-PDF).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="auto-remind-toggle">
            <input
              type="checkbox"
              className="mt-1 rounded border-slate-300"
              checked={autoRemind}
              onChange={(e) => setAutoRemind(e.target.checked)}
            />
            <span>
              <Label className="cursor-pointer">Zahlungserinnerung bei Rückläufern automatisch senden</Label>
              <p className="text-xs text-slate-500 mt-0.5">
                Nur wenn noch keine Mahnung vorliegt. Fehlende E-Mail-Adressen erscheinen in der Auswertung.
              </p>
            </span>
          </label>
          <input type="file" accept=".xml,text/xml,application/xml" ref={fileRef}
                 onChange={handleUpload} className="hidden" data-testid="statement-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading}
                  data-testid="statement-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            CAMT.053-Datei auswählen
          </Button>
        </CardContent>
      </Card>

      {result && (
        <>
          <div className="grid gap-4 md:grid-cols-3" data-testid="statement-result">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Zugeordnet ({result.matched.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-1 max-h-[300px] overflow-y-auto">
                {result.matched.length === 0 && <p className="text-slate-400 italic">Keine</p>}
                {result.matched.map((m, i) => (
                  <div key={i} className="flex justify-between gap-2 border-b border-slate-100 pb-1">
                    <span className="font-mono">{m.invoice_number}</span>
                    <span className="text-emerald-700 font-semibold whitespace-nowrap">{formatDeNumber(m.amount)} €</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-rose-600" /> Rücklastschriften ({result.chargebacks.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-1 max-h-[300px] overflow-y-auto">
                {result.chargebacks.length === 0 && <p className="text-slate-400 italic">Keine</p>}
                {result.chargebacks.map((c, i) => (
                  <div key={i} className="border-b border-slate-100 pb-1">
                    <span className="font-mono">{c.invoice_number}</span>{' '}
                    <Badge variant="outline" className="text-[10px] border-rose-200 bg-rose-50 text-rose-600">{c.reason}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-brand-red" /> Nicht zuordenbar ({result.unmatched.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-1 max-h-[300px] overflow-y-auto">
                {result.unmatched.length === 0 && <p className="text-slate-400 italic">Keine</p>}
                {result.unmatched.map((u, i) => (
                  <div key={i} className="border-b border-slate-100 pb-1">
                    <span className={u.credit ? 'text-emerald-700' : 'text-rose-600'}>
                      {u.credit ? '+' : '−'}{formatDeNumber(u.amount)} €
                    </span>{' '}
                    <span className="text-slate-500">{u.reference || 'ohne Verwendungszweck'}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {result.auto_remind && result.chargebacks.length > 0 && (
            <Card data-testid="statement-reminders">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <MailWarning className="w-4 h-4 text-amber-600" />
                  Automatische Mahnungen
                  <span className="text-slate-400 font-normal">
                    ({remindSent.length} gesendet · {remindQueued.length} in Freigabe · {remindFailed.length} fehlgeschlagen · {remindSkipped.length} übersprungen)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2 max-h-[240px] overflow-y-auto">
                {remindSent.map((r, i) => (
                  <div key={`s-${i}`} className="text-emerald-700">
                    {r.invoice_number} → {r.recipient}
                  </div>
                ))}
                {remindQueued.map((r, i) => (
                  <div key={`q-${i}`} className="text-amber-700">
                    {r.invoice_number} → Freigabe-Queue ({r.recipient})
                  </div>
                ))}
                {remindFailed.map((r, i) => (
                  <div key={`f-${i}`} className="text-rose-600">
                    {r.invoice_number}: {r.error}
                  </div>
                ))}
                {remindSkipped.map((r, i) => (
                  <div key={`k-${i}`} className="text-slate-400">
                    {r.invoice_number}: {r.skip_reason}
                  </div>
                ))}
                {remindSent.length + remindQueued.length + remindFailed.length + remindSkipped.length === 0 && (
                  <p className="text-slate-400 italic">Keine Mahnungen ausgelöst</p>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {bewegungen.length > 0 && (
        <Card data-testid="statement-movements">
          <CardHeader className="pb-2 space-y-2">
            <CardTitle className="text-sm">Letzte Kontobewegungen</CardTitle>
            {bewegungsKonten.length > 1 && (
              <select
                className="flex h-9 w-full max-w-sm rounded-md border border-input bg-background px-3 text-sm"
                value={bewegungsBank}
                onChange={(e) => setBewegungsBank(e.target.value)}
                data-testid="statement-movement-bank"
                aria-label="Kontobewegungen nach Bank filtern"
              >
                <option value="">Alle Banken</option>
                {bewegungsKonten.map((konto) => (
                  <option key={konto.id} value={konto.id} label={konto.label} />
                ))}
              </select>
            )}
          </CardHeader>
          <CardContent className="text-xs space-y-1 max-h-[320px] overflow-y-auto">
            {sichtbareBewegungen.map((b, i) => (
              <div
                key={`${b.connection_id || 'import'}-${b.booking_date}-${i}`}
                className="flex justify-between gap-2 border-b border-slate-100 pb-1"
                data-testid={`statement-movement-${b.connection_id || 'import'}`}
              >
                <span className="text-slate-500 whitespace-nowrap">
                  {b.booking_date ? b.booking_date.split('-').reverse().join('.') : ''}
                </span>
                <span className="text-slate-500 whitespace-nowrap">{buchungsbank(b)}</span>
                <span className="flex-1 truncate text-slate-600">{b.reference || 'ohne Verwendungszweck'}</span>
                <span className={b.credit ? 'text-emerald-700' : 'text-rose-600'}>
                  {b.credit ? '+' : '−'}{formatDeNumber(b.amount)} €
                </span>
                {b.status === 'chargeback' && (
                  <Badge variant="outline" className="text-[10px] border-rose-200 bg-rose-50 text-rose-600">Rücklastschrift</Badge>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SepaStatementTab;
